import { useCallback, useEffect, useRef, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { CreditCard, Wallet, RefreshCw, FileImage, FileText, X } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toast } from "sonner";

function Kpi({ label, value, sub, testid, icon: Icon, accent }) {
  return (
    <Card data-testid={testid} className="border-slate-200">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold">
          {Icon && <Icon className={`w-3.5 h-3.5 ${accent || "text-slate-400"}`} />}{label}
        </div>
        <div className="mt-2 font-display text-2xl font-bold tracking-tight">{value}</div>
        {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
}

export default function StoreDashboard() {
  const [d, setD] = useState(null);
  const [store, setStore] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [busy, setBusy] = useState(false);
  const rootRef = useRef();

  useEffect(() => { api.get("/store/me").then((r) => setStore(r.data)); }, []);

  // One-time trial-cap nudge: if we're on the Free Trial and today's cap is hit, show a
  // persistent toast with an Upgrade CTA. Only fires once per browser session.
  useEffect(() => {
    if (sessionStorage.getItem("pp_trial_toast_shown")) return;
    api.get("/store/trial-status").then(({ data }) => {
      if (data?.is_trial && data.remaining_today === 0) {
        sessionStorage.setItem("pp_trial_toast_shown", "1");
        toast.warning(
          `You've hit today's free-trial cap (${data.used_today}/${data.daily_limit} prints). Customers can't place new orders until midnight IST.`,
          {
            duration: 20000,
            action: {
              label: "Upgrade",
              onClick: () => { window.location.href = "/store/subscription"; },
            },
          }
        );
      }
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/store/analytics", { params });
      setD(data);
    } finally { setBusy(false); }
  }, [dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  const reset = () => { setDateFrom(""); setDateTo(""); };

  const exportAsImage = async () => {
    if (!rootRef.current) return;
    const t = toast.loading("Rendering dashboard as PNG…");
    try {
      const canvas = await html2canvas(rootRef.current, { backgroundColor: "#f8fafc", scale: 2, useCORS: true });
      const link = document.createElement("a");
      link.download = `printpulse-${(store?.slug || "store")}-report-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Downloaded", { id: t });
    } catch (e) { toast.error(`Export failed: ${e.message}`, { id: t }); }
  };

  const exportAsPdf = async () => {
    if (!rootRef.current) return;
    const t = toast.loading("Building PDF…");
    try {
      const canvas = await html2canvas(rootRef.current, { backgroundColor: "#ffffff", scale: 2, useCORS: true });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const ratio = canvas.width / canvas.height;
      let w = pageW - 40, h = w / ratio;
      if (h > pageH - 40) { h = pageH - 40; w = h * ratio; }
      pdf.addImage(imgData, "PNG", (pageW - w) / 2, 20, w, h);
      pdf.save(`printpulse-${(store?.slug || "store")}-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF downloaded", { id: t });
    } catch (e) { toast.error(`Export failed: ${e.message}`, { id: t }); }
  };

  if (!d) return <div className="p-8 text-slate-400">Loading…</div>;

  const paymentBreakdown = [
    { name: "Online", value: d.online_revenue, count: d.online_orders, fill: "#10B981" },
    { name: "Cash", value: d.cash_revenue, count: d.cash_orders, fill: "#F59E0B" },
  ].filter((x) => x.value > 0 || x.count > 0);
  const pie = [
    { name: "B&W", value: d.bw_pages, fill: "#0F172A" },
    { name: "Colour", value: d.color_pages, fill: "#F97316" },
  ];
  const now = new Date();
  const exportedAt = now.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const rangeLabel = dateFrom || dateTo ? `${dateFrom || "beginning"} → ${dateTo || "today"}` : "All time";

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="store-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {store?.logo_url && <img src={store.logo_url} alt="logo" className="w-12 h-12 rounded-xl object-cover border border-slate-200" data-testid="dashboard-logo" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
          <div><h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight" data-testid="dashboard-title">{store?.name || "Store overview"}</h1><p className="text-slate-500 text-sm">Your shop today, this month, and beyond.</p></div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportAsImage} data-testid="export-png"><FileImage className="w-3.5 h-3.5 mr-1.5" />PNG</Button>
          <Button variant="outline" size="sm" onClick={exportAsPdf} data-testid="export-pdf"><FileText className="w-3.5 h-3.5 mr-1.5" />PDF</Button>
        </div>
      </div>

      {/* Filter bar */}
      <Card className="border-slate-200">
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider mr-1">Date range</span>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-[145px] text-sm" data-testid="dash-date-from" />
          <span className="text-slate-400 text-xs">→</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-[145px] text-sm" data-testid="dash-date-to" />
          {(dateFrom || dateTo) && <Button variant="ghost" size="sm" onClick={reset} className="h-9 text-slate-500" data-testid="dash-filter-reset"><X className="w-3 h-3 mr-1" />Clear</Button>}
          <Button variant="outline" size="sm" onClick={load} disabled={busy} className="h-9 ml-auto" data-testid="dash-refresh"><RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} />Refresh</Button>
        </CardContent>
      </Card>

      <div ref={rootRef} className="space-y-6 bg-white p-5 rounded-2xl border border-slate-200">
        {/* Branded report header — always visible, ends up on every PNG/PDF export */}
        <div className="flex items-center gap-4 pb-4 border-b border-slate-200" data-testid="report-header">
          {store?.logo_url
            ? <img src={store.logo_url} alt="" className="w-14 h-14 rounded-xl object-cover border border-slate-200 flex-shrink-0" crossOrigin="anonymous" />
            : <img src="/logo.png" alt="Print Plus" className="w-14 h-14 object-contain flex-shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-xl leading-tight">{store?.name || "Store"}</div>
            <div className="text-xs text-slate-500 mt-0.5">{[store?.city, store?.state].filter(Boolean).join(", ") || "Print Plus by AI Upaya"}</div>
          </div>
          <div className="text-right text-xs text-slate-500 flex-shrink-0">
            <div className="uppercase tracking-widest font-semibold text-[10px] text-slate-400">Report</div>
            <div className="font-mono text-slate-700">{exportedAt}</div>
            <div className="text-[11px] mt-0.5">Range: <span className="font-medium text-slate-700">{rangeLabel}</span></div>
          </div>
        </div>

        {/* Payment KPIs — highlighted at top */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi label="Online paid" value={fmtINR(d.online_revenue)} sub={`${d.online_orders} orders`} testid="kpi-online" icon={CreditCard} accent="text-emerald-500" />
          <Kpi label="Cash collected" value={fmtINR(d.cash_revenue)} sub={`${d.cash_orders} cash orders${d.cash_pending_count ? ` · ${d.cash_pending_count} pending` : ""}`} testid="kpi-cash" icon={Wallet} accent="text-amber-500" />
          <Kpi label="Total Revenue" value={fmtINR(d.total_revenue)} sub={`AOV ${fmtINR(d.avg_order_value)}`} testid="kpi-total-revenue" />
          <Kpi label="Total Orders" value={d.total_orders} sub={`${d.total_pages} pages`} testid="kpi-total-orders" />
        </div>

        {/* Secondary KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi label="Today Orders" value={d.today_orders} sub={`${d.today_pages} pages`} testid="kpi-today-orders" />
          <Kpi label="Today Revenue" value={fmtINR(d.today_revenue)} testid="kpi-today-revenue" />
          <Kpi label="Month Orders" value={d.month_orders} sub={`${d.month_pages} pages`} testid="kpi-month-orders" />
          <Kpi label="Pending / Done" value={`${d.pending_jobs} / ${d.completed_jobs}`} sub={`${d.failed_jobs} failed`} testid="kpi-jobs" />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 border-slate-200">
            <CardHeader><CardTitle className="font-display">Daily revenue — Online vs Cash</CardTitle></CardHeader>
            <CardContent className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.daily} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip formatter={(v) => fmtINR(v)} /><Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="online_revenue" name="Online" fill="#10B981" stackId="rev" />
                  <Bar dataKey="cash_revenue" name="Cash" fill="#F59E0B" stackId="rev" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardHeader><CardTitle className="font-display">Online vs Cash</CardTitle></CardHeader>
            <CardContent className="h-72">
              {paymentBreakdown.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 5, right: 5, bottom: 30, left: 5 }}>
                    <Pie data={paymentBreakdown} dataKey="value" nameKey="name" outerRadius={70} label={(e) => `${e.name} ${((e.percent || 0) * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                      {paymentBreakdown.map((e, i) => <Cell key={i} fill={e.fill} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [fmtINR(v), n]} />
                    <Legend wrapperStyle={{ fontSize: 11 }} verticalAlign="bottom" />
                  </PieChart>
                </ResponsiveContainer>
              ) : <div className="h-full grid place-items-center text-sm text-slate-400">No revenue in the selected window yet.</div>}
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 border-slate-200">
            <CardHeader><CardTitle className="font-display">Daily orders & revenue</CardTitle></CardHeader>
            <CardContent className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={d.daily} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#F97316" strokeWidth={2} />
                  <Line type="monotone" dataKey="orders" name="Orders" stroke="#3B82F6" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardHeader><CardTitle className="font-display">B&W vs Colour</CardTitle></CardHeader>
            <CardContent className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 5, right: 5, bottom: 30, left: 5 }}>
                  <Pie data={pie} dataKey="value" nameKey="name" outerRadius={70} label={(e) => `${e.name} ${((e.percent || 0) * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                    {pie.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} verticalAlign="bottom" />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
